import axios from "axios";

export const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({ baseURL: API, withCredentials: true, timeout: 45000 });

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("ab_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

// Langganan admin habis -> arahkan ke halaman perpanjangan (bukan error berulang)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 402) {
      const path = window.location.pathname;
      if (path !== "/langganan" && path !== "/login" && path !== "/daftar") {
        window.location.replace("/langganan");
      }
    }
    return Promise.reject(err);
  },
);

export function errMsg(e) {
  if (e?.code === "ECONNABORTED") return "Permintaan ke router terlalu lama. Coba lagi.";
  if (e?.code === "ERR_NETWORK" || (!e?.response && e?.message === "Network Error")) {
    return "Tidak bisa menghubungi server aplikasi (Network Error). Pastikan Anda membuka alamat aplikasi yang terbaru dan koneksi internet aktif.";
  }
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (d?.msg) return d.msg;
  return e?.message || "Terjadi kesalahan";
}

/** true bila error karena server tidak terjangkau (bukan salah password). */
export const isNetworkError = (e) =>
  e?.code === "ERR_NETWORK" || (!e?.response && (e?.message === "Network Error" || e?.code === "ECONNABORTED"));

/** Cek apakah halaman dibuka dari alamat yang sama dengan alamat server API. */
export function originMismatch() {
  try {
    return new URL(BASE).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/** Ping ringan ke backend untuk menampilkan status koneksi di halaman login. */
export async function pingServer() {
  const started = Date.now();
  try {
    const r = await fetch(`${API}/`, { method: "GET", credentials: "include" });
    return { ok: r.ok, status: r.status, ms: Date.now() - started };
  } catch (e) {
    return { ok: false, status: 0, ms: Date.now() - started, error: e?.message || "gagal" };
  }
}

export const rp = (n) =>
  "Rp " + Number(n || 0).toLocaleString("id-ID", { maximumFractionDigits: 0 });

export const bytes = (n) => {
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = Number(n || 0);
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

export const bps = (n) => {
  const u = ["bps", "Kbps", "Mbps", "Gbps"];
  let i = 0;
  let v = Number(n || 0);
  while (v >= 1000 && i < u.length - 1) { v /= 1000; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
};

export const dt = (s) => (s ? new Date(s).toLocaleString("id-ID") : "-");

export function exportCsv(filename, rows) {
  if (!rows?.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(","),
    ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(",")),
  ].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** Normalisasi nomor WA Indonesia: 08xx / +62xx / 62xx -> 62xx (format wa.me). */
export const waNumber = (phone) => {
  const d = String(phone || "").replace(/\D/g, "");
  if (!d) return "";
  if (d.startsWith("0")) return "62" + d.slice(1);
  if (d.startsWith("62")) return d;
  if (d.startsWith("8")) return "62" + d;
  return d;
};

export const waLink = (phone, text) =>
  `https://wa.me/${waNumber(phone)}${text ? `?text=${encodeURIComponent(text)}` : ""}`;

/**
 * Buka WhatsApp dengan cara yang tetap jalan di aplikasi terpasang (PWA/standalone),
 * di mana <a target="_blank"> sering diabaikan sistem. Kembalikan false bila nomor kosong.
 */
export const openWa = (phone, text) => {
  const num = waNumber(phone);
  if (!num) return false;
  const url = waLink(num, text);
  const standalone =
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches) ||
    window.navigator?.standalone === true;
  if (standalone) {
    window.location.href = url;
    return true;
  }
  let win = null;
  try {
    // JANGAN pakai fitur "noopener" di sini: window.open akan selalu mengembalikan
    // null sehingga fallback di bawah ikut jalan dan halaman aplikasi berpindah.
    win = window.open(url, "_blank");
    if (win) {
      try { win.opener = null; } catch (e) { /* lintas-origin, aman diabaikan */ }
    }
  } catch (e) {
    win = null;
  }
  if (!win) window.location.href = url; // popup diblokir -> buka di tab ini
  return true;
};

/**
 * Buka halaman lain di tab baru. Sama seperti openWa: jangan pakai fitur "noopener"
 * agar window.open tidak selalu mengembalikan null, dan tetap jalan di aplikasi
 * terpasang (PWA) yang sering memblokir tab baru.
 */
export const openTab = (url) => {
  if (!url) return false;
  const standalone =
    (typeof window !== "undefined" && window.matchMedia?.("(display-mode: standalone)")?.matches) ||
    window.navigator?.standalone === true;
  if (standalone) {
    window.location.href = url;
    return true;
  }
  let win = null;
  try {
    win = window.open(url, "_blank");
    if (win) {
      try { win.opener = null; } catch (e) { /* lintas-origin, aman diabaikan */ }
    }
  } catch (e) {
    win = null;
  }
  if (!win) window.location.href = url;
  return true;
};

/** Salin teks ke papan klip dengan fallback untuk browser lama / konteks non-HTTPS. */
export const copyText = async (text) => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // lanjut ke fallback
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (e) {
    return false;
  }
};
